import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import {
  Flame,
  Star,
  TrendingUp,
} from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/hooks/useTheme';
import { AuthProvider } from '@/hooks/useAuth';
import { DashboardProvider } from '@/hooks/useDashboardContext';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireNetwork } from '@/components/RequireNetwork';
import { LoginPage } from '@/modules/auth/LoginPage';
import { TodayPage } from '@/modules/today/TodayPage';
import { OverviewPage } from '@/modules/overview/OverviewPage';
import { OutletsPage } from '@/modules/outlets/OutletsPage';
import { OutletDetailPage } from '@/modules/outlets/OutletDetailPage';
import { SalesPage } from '@/modules/sales/SalesPage';
import { ItemDetailPage } from '@/modules/sales/ItemDetailPage';
import { TargetJourney } from '@/modules/journey/TargetJourney';
import { GrowthJourney } from '@/modules/growth/GrowthJourney';
import { AggregatorsPage } from '@/modules/aggregators/AggregatorsPage';
import { AggregatorsCompetitivePage } from '@/modules/aggregators-competitive/AggregatorsCompetitivePage';
import { ReviewsPage } from '@/modules/reviews/ReviewsPage';
import { MetaPage } from '@/modules/meta/MetaPage';
import { LinkedinPage } from '@/modules/linkedin/LinkedinPage';
import { StudioPage } from '@/modules/studio/StudioPage';
import { CompetitivePage } from '@/modules/competitive/CompetitivePage';
import { GapReportsPage } from '@/modules/competitive/GapReportsPage';
import { CompetitorBoostsPage } from '@/modules/competitive/CompetitorBoostsPage';
import { InfluencersPage } from '@/modules/influencers/InfluencersPage';
import { ChecklistPage } from '@/modules/checklist/ChecklistPage';
import { RatingsPage } from '@/modules/ratings/RatingsPage';
import { BusinessReviewPage } from '@/modules/review/BusinessReviewPage';
import { ModuleStub } from '@/modules/_placeholder/ModuleStub';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider delayDuration={200} skipDelayDuration={500}>
            <DashboardProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    element={
                      <RequireNetwork>
                        <RequireAuth>
                          <AppShell />
                        </RequireAuth>
                      </RequireNetwork>
                    }
                  >
                  <Route index element={<TodayPage />} />
                  <Route path="overview" element={<OverviewPage />} />
                  <Route path="review" element={<BusinessReviewPage />} />
                  <Route path="outlets" element={<OutletsPage />} />
                  <Route path="outlets/:outletId" element={<OutletDetailPage />} />
                  <Route path="sales" element={<SalesPage />} />
                  <Route path="sales/items/:itemId" element={<ItemDetailPage />} />
                  <Route path="journey" element={<TargetJourney />} />
                  <Route path="growth" element={<GrowthJourney />} />
                  <Route path="aggregators" element={<AggregatorsPage />} />
                  <Route path="aggregators-competitive" element={<AggregatorsCompetitivePage />} />
                  <Route
                    path="ratings"
                    element={
                      <ModuleStub
                        title="Ratings"
                        description="Current vs previous rating per outlet per source; drop detection feeds alerts."
                        icon={Star}
                        phase="Phase 3"
                      />
                    }
                  />
                  <Route path="reviews" element={<ReviewsPage />} />
                  <Route path="ratings" element={<RatingsPage />} />
                  <Route path="meta" element={<MetaPage />} />
                  <Route path="linkedin" element={<LinkedinPage />} />
                  <Route path="studio" element={<StudioPage />} />
                  <Route path="competitive" element={<CompetitivePage />} />
                  <Route path="gap-reports" element={<GapReportsPage />} />
                  <Route path="competitor-boosts" element={<CompetitorBoostsPage />} />
                  <Route path="influencers" element={<InfluencersPage />} />
                  <Route path="checklist" element={<ChecklistPage />} />
                  <Route
                    path="roi"
                    element={
                      <ModuleStub
                        title="ROI"
                        description="Spend (marketing + aggregator ads) against attributable sales, per channel and outlet."
                        icon={TrendingUp}
                        phase="Phase 3"
                      />
                    }
                  />
                  <Route
                    path="alerts"
                    element={
                      <ModuleStub
                        title="Alerts"
                        description="Notification center mirroring what Slack fires."
                        icon={Flame}
                        phase="Phase 4"
                      />
                    }
                  />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </BrowserRouter>
            </DashboardProvider>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
